import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Facebook, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Admin-only: link the Facebook Page for Lead Ads and show Meta's raw reply. */
export default function MetaConnectCard() {
  const [result, setResult] = useState(null);
  const statusQ = useQuery({
    queryKey: ["meta", "status"],
    queryFn: async () => (await api.get("/meta/status")).data,
  });
  const linkMut = useMutation({
    mutationFn: async () => (await api.post("/meta/subscribe")).data,
    onSuccess: (d) => {
      setResult(d);
      d.ok ? toast.success("Facebook Page linked") : toast.error("Facebook rejected the request");
    },
    onError: (e) => {
      setResult({ ok: false, response: e?.response?.data || String(e) });
      toast.error(e?.response?.data?.detail || "Link failed");
    },
  });
  const retryMut = useMutation({
    mutationFn: async () => (await api.post("/meta/retry")).data,
    onSuccess: (d) => { toast.success(`Retried ${d.retried} lead(s)`); statusQ.refetch(); },
  });
  const s = statusQ.data;
  const webhookUrl = `${window.location.origin}/api/webhooks/meta`;

  return (
    <section className="soft-card space-y-3 p-5" data-testid="meta-connect-card">
      <div className="flex items-center gap-2">
        <Facebook className="h-5 w-5 text-[#1877F2]" />
        <h3 className="text-base font-bold text-navy">Facebook Lead Ads</h3>
      </div>
      <div className="grid gap-1.5 text-sm">
        <Flag ok={s?.page_token_set} label="Page access token" tid="meta-flag-page-token" />
        <Flag ok={s?.verify_token_set} label="Webhook verify token" tid="meta-flag-verify-token" />
        <div className="text-xs text-slate-500">
          Webhook URL: <code className="break-all rounded bg-slate-100 px-1.5 py-0.5" data-testid="meta-webhook-url">{webhookUrl}</code>
        </div>
        <div className="text-xs text-slate-500" data-testid="meta-live-count">Live leads received: <b>{s?.live_leads ?? "—"}</b></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="meta-link-btn" className="bg-[#1877F2] text-white hover:brightness-110" disabled={linkMut.isPending} onClick={() => linkMut.mutate()}>
          {linkMut.isPending ? "Linking…" : "Link Facebook Page"}
        </Button>
        <Button data-testid="meta-retry-btn" variant="outline" disabled={retryMut.isPending} onClick={() => retryMut.mutate()}>
          <RotateCcw className="h-4 w-4" /> Retry failed leads
        </Button>
      </div>
      {result && (
        <pre data-testid="meta-link-result" className={`max-h-72 overflow-auto rounded-xl p-3 text-[11px] ${result.ok ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`}>
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
      {!!s?.recent_events?.length && (
        <div className="space-y-1" data-testid="meta-recent-events">
          <div className="text-xs font-semibold text-slate-500">Recent Facebook events</div>
          {s.recent_events.slice(0, 5).map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-2 py-1 text-[11px]">
              <span className="truncate">{e.value?.leadgen_id || "—"} · {new Date(e.created_at).toLocaleString()}</span>
              <span className={e.status === "error" ? "font-semibold text-rose-600" : "text-emerald-700"} title={e.error || ""}>{e.status}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Flag({ ok, label, tid }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <div className="flex items-center gap-1.5" data-testid={tid}>
      <Icon className={`h-4 w-4 ${ok ? "text-emerald-500" : "text-rose-500"}`} />
      {label} {ok ? "configured" : "missing"}
    </div>
  );
}
